/* CreateRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const _ = require("lodash");

// OS Family options for dropdown
const OS_FAMILY_OPTIONS = [
    {label: "Windows", value: "Windows"},
    {label: "Debian", value: "Debian"},
];

// Validation message describing required field combinations
const VALIDATION_MESSAGE =
    "Name is required and must be unique. You must also complete Manufacturer, Model, and Device Type, and either OS Name and OS Version, or OS Release Name.";

/**
 * Renderer for the platform creation dialog.
 * Handles form rendering, validation, and platform creation.
 */
class CreateRenderer {
    constructor(model, previousFormData, fieldOptions) {
        this._model = model;
        this._previousFormData = previousFormData || {};
        this._fieldOptions = fieldOptions || {};
        this._rootElem = document.createElement("platform-create-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = "Add IoT Platform";

        this._errorContainer = document.createElement("error-container");
        this._errorContainer.style.display = "none";

        this._staticMessageContainer = document.createElement("static-message-container");
        this._staticMessageContainer.innerText = VALIDATION_MESSAGE;

        this._formContainer = document.createElement("form-container");
        this._buttonContainer = document.createElement("button-container");

        // Create all input fields
        this._nameField = new InputTextControl({
            darkTheme: true,
            label: "Name",
            labelShown: true,
            value: this._previousFormData.name || "",
        });

        this._manufacturerField = new ComboBoxControl({
            darkTheme: true,
            label: "Manufacturer",
            labelShown: true,
            options: this._fieldOptions.manufacturerOpts || [],
            value: this._previousFormData.manufacturer || "",
        });

        this._modelField = new ComboBoxControl({
            darkTheme: true,
            label: "Model",
            labelShown: true,
            options: this._fieldOptions.modelOpts || [],
            value: this._previousFormData.model || "",
        });

        this._deviceTypeField = new ComboBoxControl({
            darkTheme: true,
            label: "IoT Device Type",
            labelShown: true,
            options: this._fieldOptions.deviceTypeOpts || [],
            value: this._previousFormData.deviceType || "",
        });

        this._osNameField = new InputTextControl({
            darkTheme: true,
            label: "OS Name",
            labelShown: true,
            value: this._previousFormData.osName || "",
        });

        this._osFamilyField = new ComboBoxControl({
            darkTheme: true,
            label: "OS Family",
            labelShown: true,
            options: OS_FAMILY_OPTIONS,
            value: this._previousFormData.osFamily || "",
        });

        this._osReleaseNameField = new InputTextControl({
            darkTheme: true,
            label: "OS Release Name",
            labelShown: true,
            value: this._previousFormData.osReleaseName || "",
        });

        this._osVersionField = new InputTextControl({
            darkTheme: true,
            label: "OS Version",
            labelShown: true,
            value: this._previousFormData.osVersion || "",
        });

        this._machineArchitectureField = new InputTextControl({
            darkTheme: true,
            label: "Machine Architecture",
            labelShown: true,
            value: this._previousFormData.machineArchitecture || "",
        });

        this._languageField = new InputTextControl({
            darkTheme: true,
            label: "OS Language",
            labelShown: true,
            value: this._previousFormData.language || "",
        });

        this._localeField = new InputTextControl({
            darkTheme: true,
            label: "Locale",
            labelShown: true,
            value: this._previousFormData.locale || "",
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            theme: "additive",
            disabled: true,
            active: true,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._validateForm = this._validateForm.bind(this);
        this._createPlatform = this._createPlatform.bind(this);

        this._registerListeners();
    }

    _registerListeners() {
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._nameField.on(
            "UserKeyDown",
            (this._checkForImpliedConfirm = this._checkForImpliedConfirm.bind(this))
        );

        // Add listeners to all fields that affect validation
        this._nameField.on("UserValue", this._validateForm);
        this._manufacturerField.on("UserValue", this._validateForm);
        this._modelField.on("UserValue", this._validateForm);
        this._deviceTypeField.on("UserValue", this._validateForm);
        this._osNameField.on("UserValue", this._validateForm);
        this._osVersionField.on("UserValue", this._validateForm);
        this._osReleaseNameField.on("UserValue", this._validateForm);
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._nameField.removeAllListeners("UserKeyDown");
        this._nameField.removeAllListeners("UserValue");
        this._manufacturerField.removeAllListeners("UserValue");
        this._modelField.removeAllListeners("UserValue");
        this._deviceTypeField.removeAllListeners("UserValue");
        this._osNameField.removeAllListeners("UserValue");
        this._osVersionField.removeAllListeners("UserValue");
        this._osReleaseNameField.removeAllListeners("UserValue");
    }

    _onConfirmButtonClick() {
        this._createPlatform();
    }

    _onCancelButtonClick() {
        this._model.cancelAction();
    }

    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            if (this._isFormValid()) {
                this._createPlatform();
            }
        }
    }

    _validateForm() {
        this._confirmButton.disabled = !this._isFormValid();
    }

    _isFormValid() {
        const name = this._nameField.getValue();
        const manufacturer = this._manufacturerField.getValue();
        const model = this._modelField.getValue();
        const deviceType = this._deviceTypeField.getValue();
        const osName = this._osNameField.getValue();
        const osVersion = this._osVersionField.getValue();
        const osReleaseName = this._osReleaseNameField.getValue();

        // Name is required
        if (_.isEmpty(name) || name.trim().length === 0) {
            return false;
        }

        // Check if all fields in combination 1 are filled:
        // Manufacturer + Model + IoT Device Type + OS Name + OS Version
        const combination1Valid =
            !_.isEmpty(manufacturer) &&
            manufacturer.trim().length > 0 &&
            !_.isEmpty(model) &&
            model.trim().length > 0 &&
            !_.isEmpty(deviceType) &&
            deviceType.trim().length > 0 &&
            !_.isEmpty(osName) &&
            osName.trim().length > 0 &&
            !_.isEmpty(osVersion) &&
            osVersion.trim().length > 0;

        // Check if all fields in combination 2 are filled:
        // Manufacturer + Model + IoT Device Type + OS Release Name
        const combination2Valid =
            !_.isEmpty(manufacturer) &&
            manufacturer.trim().length > 0 &&
            !_.isEmpty(model) &&
            model.trim().length > 0 &&
            !_.isEmpty(deviceType) &&
            deviceType.trim().length > 0 &&
            !_.isEmpty(osReleaseName) &&
            osReleaseName.trim().length > 0;

        // Form is valid if either combination is satisfied
        return combination1Valid || combination2Valid;
    }

    /**
     * Cleans up all resources, event listeners, and DOM elements.
     * @returns {Promise} Promise that resolves when shutdown is complete
     */
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._nameField.shutdown(),
            this._manufacturerField.shutdown(),
            this._modelField.shutdown(),
            this._deviceTypeField.shutdown(),
            this._osNameField.shutdown(),
            this._osFamilyField.shutdown(),
            this._osReleaseNameField.shutdown(),
            this._osVersionField.shutdown(),
            this._machineArchitectureField.shutdown(),
            this._languageField.shutdown(),
            this._localeField.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
        ];

        return Promise.allSettled(promArr).then(
            () => {
                this._nameField = undefined;
                this._manufacturerField = undefined;
                this._modelField = undefined;
                this._deviceTypeField = undefined;
                this._osNameField = undefined;
                this._osFamilyField = undefined;
                this._osReleaseNameField = undefined;
                this._osVersionField = undefined;
                this._machineArchitectureField = undefined;
                this._languageField = undefined;
                this._localeField = undefined;
                this._confirmButton = undefined;
                this._cancelButton = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._formContainer.remove();
                this._formContainer = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._staticMessageContainer.remove();
                this._staticMessageContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "CreateRenderer.shutdown() error");
            }
        );
    }

    /**
     * Renders the platform creation form.
     * @returns {Promise} Promise that resolves when rendering is complete
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                // Add title
                this._rootElem.appendChild(this._titleComponent);

                // Add static message
                this._rootElem.appendChild(this._staticMessageContainer);

                // Helper function to create field row with label
                const createFieldRow = (label, field) => {
                    const row = document.createElement("field-row");
                    if (label === "Name") {
                        row.classList.add("full-width-item");
                    }
                    const labelElem = document.createElement("label");
                    labelElem.innerText = label + ":";
                    row.appendChild(labelElem);
                    row.appendChild(field.renderer.rootElement);
                    return row;
                };

                // Add all input fields to form container with labels
                this._formContainer.appendChild(createFieldRow("Name", this._nameField));
                this._formContainer.appendChild(
                    createFieldRow("Manufacturer", this._manufacturerField)
                );
                this._formContainer.appendChild(
                    createFieldRow("IoT Device Type", this._deviceTypeField)
                );
                this._formContainer.appendChild(createFieldRow("Model", this._modelField));
                this._formContainer.appendChild(createFieldRow("OS Name", this._osNameField));
                this._formContainer.appendChild(createFieldRow("OS Family", this._osFamilyField));
                this._formContainer.appendChild(
                    createFieldRow("OS Release Name", this._osReleaseNameField)
                );
                this._formContainer.appendChild(createFieldRow("OS Version", this._osVersionField));
                this._formContainer.appendChild(
                    createFieldRow("Machine Architecture", this._machineArchitectureField)
                );
                this._formContainer.appendChild(createFieldRow("OS Language", this._languageField));
                this._formContainer.appendChild(createFieldRow("Locale", this._localeField));

                this._rootElem.appendChild(this._formContainer);

                // Add buttons and error message
                this._buttonContainer.appendChild(this._errorContainer);
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
                this._rootElem.appendChild(this._buttonContainer);

                setTimeout(() => {
                    // Validate form with previous data if available
                    if (this._previousFormData && this._previousFormData.name) {
                        this._validateForm();
                    }
                    this._nameField.focus();
                    this._formContainer.scrollTo(0, 0);
                });
            }
            resolve();
        });
    }

    /**
     * Gets the root DOM element.
     * @returns {HTMLElement} The root element
     */
    get rootElement() {
        return this._rootElem;
    }

    getRootElement() {
        return this.rootElement;
    }

    /**
     * Gets the component model.
     * @returns {Object} The component model
     */
    get model() {
        return this._model;
    }

    getModel() {
        return this.model;
    }

    _createPlatform() {
        // Hide any previous error
        this._hideError();

        // Disable the save button and show loading state
        this._confirmButton.disabled = true;
        this._confirmButton.label = "Saving...";

        // Gather form data
        const formData = {
            name: this._nameField.getValue(),
            manufacturer: this._manufacturerField.getValue(),
            model: this._modelField.getValue(),
            deviceType: this._deviceTypeField.getValue(),
            osName: this._osNameField.getValue(),
            osFamily: this._osFamilyField.getValue(),
            osReleaseName: this._osReleaseNameField.getValue(),
            osVersion: this._osVersionField.getValue(),
            machineArchitecture: this._machineArchitectureField.getValue(),
            language: this._languageField.getValue(),
            locale: this._localeField.getValue(),
        };

        // Delegate to component for business logic
        this._model
            .createPlatform(formData)
            .then((result) => {
                // Success - notify component
                this._model.createAction({id: result.id, result});
            })
            .catch((err) => {
                // Error - display inline and re-enable form
                this._model.logger.warn({error: err}, "CreateRenderer._createPlatform() error.");
                this._showError(
                    "A platform with these details already exists. Change the details or open the existing platform."
                );
                this._confirmButton.label = "Save";
                this._validateForm();
            });
    }

    _showError(message) {
        this._errorContainer.innerText = message;
        this._errorContainer.style.display = "block";
    }

    _hideError() {
        this._errorContainer.style.display = "none";
        this._errorContainer.innerText = "";
    }
}

module.exports = CreateRenderer;
